import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TicketQueueTable = ({ tickets, onViewDetails, onStatusUpdate }) => {
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedTickets = [...tickets]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];

    if (typeof aVal === 'string') {
      aVal = aVal?.toLowerCase();
      bVal = bVal?.toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusBadge = (status) => {
    const statusStyles = {
      open: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'Open' },
      in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'In Progress' },
      pending_client: { bg: 'bg-purple-500/10', text: 'text-purple-600', label: 'Pending Client' },
      resolved: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Resolved' },
      closed: { bg: 'bg-gray-500/10', text: 'text-gray-600', label: 'Closed' },
      cancelled: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Cancelled' }
    };
    return statusStyles?.[status] || statusStyles?.open;
  };

  const getPriorityBadge = (priority) => {
    const priorityStyles = {
      critical: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Critical', icon: 'AlertCircle' },
      high: { bg: 'bg-orange-500/10', text: 'text-orange-600', label: 'High', icon: 'AlertTriangle' },
      medium: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', label: 'Medium', icon: 'Info' },
      low: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Low', icon: 'CheckCircle' }
    };
    return priorityStyles?.[priority] || priorityStyles?.medium;
  };

  const getSLABadge = (slaStatus) => {
    const slaStyles = {
      within_sla: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Within SLA' },
      approaching_breach: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'Approaching Breach' },
      breached: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Breached' }
    };
    return slaStyles?.[slaStatus] || slaStyles?.within_sla;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SortableHeader = ({ field, label }) => (
    <th
      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {label}
        {sortField === field && (
          <Icon
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
            size={14}
          />
        )}
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <SortableHeader field="ticketNumber" label="Ticket #" />
            <SortableHeader field="companyName" label="Client" />
            <SortableHeader field="subject" label="Subject" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Category</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Priority</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">SLA</th>
            <SortableHeader field="assignedAgent" label="Assigned To" />
            <SortableHeader field="createdAt" label="Created" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedTickets?.length === 0 ? (
            <tr>
              <td colSpan="10" className="text-center py-8 text-muted-foreground">
                No support tickets found
              </td>
            </tr>
          ) : (
            sortedTickets?.map((ticket) => {
              const statusBadge = getStatusBadge(ticket?.ticketStatus);
              const priorityBadge = getPriorityBadge(ticket?.priority);
              const slaBadge = getSLABadge(ticket?.slaStatus);

              return (
                <tr key={ticket?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 text-sm font-medium text-foreground">
                    {ticket?.ticketNumber}
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{ticket?.companyName}</p>
                      <p className="text-xs text-muted-foreground">{ticket?.clientContactName}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="max-w-xs">
                      <p className="text-sm font-medium text-foreground truncate">{ticket?.subject}</p>
                      <p className="text-xs text-muted-foreground truncate">{ticket?.description}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {ticket?.category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${priorityBadge?.bg} ${priorityBadge?.text}`}>
                      <Icon name={priorityBadge?.icon} size={12} />
                      {priorityBadge?.label}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusBadge?.bg} ${statusBadge?.text}`}>
                      {statusBadge?.label}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${slaBadge?.bg} ${slaBadge?.text}`}>
                      {slaBadge?.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {ticket?.assignedAgent || 'Unassigned'}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {formatDate(ticket?.createdAt)}
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewDetails?.(ticket)}
                    >
                      <Icon name="Eye" size={14} className="mr-1" />
                      View
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TicketQueueTable;